// Pure file-tree helpers (Requirements 6.2, 6.5, 1.4).
//
// `flattenFileSystemTree` is the inverse of `buildFileSystemTree` from
// `services/webcontainer.ts` (which we reuse rather than duplicate): it walks a
// nested WebContainer `FileSystemTree` back into a flat path -> contents map,
// normalizing leading slashes so the build/flatten round-trip is exact.
//
// `extractFileContents` derives the flat File_Contents default map from a
// Problem_Template's Source_Tree, preserving each node's declared path.

import type { FileSystemTree } from '@webcontainer/api';
import type { FileTemplate } from '../../data/machine-coding-templates/types';

// Re-export so callers that build trees can reach the canonical implementation
// from a single helpers module.
export { buildFileSystemTree } from '../../services/webcontainer';

/**
 * Flatten a nested `FileSystemTree` into a flat `{ [path]: contents }` map.
 *
 * Paths use forward slashes with NO leading slash, matching the normalized form
 * produced by `buildFileSystemTree` (which strips leading slashes), so that
 * `flattenFileSystemTree(buildFileSystemTree(m))` equals `m` normalized.
 */
export function flattenFileSystemTree(tree: FileSystemTree): { [path: string]: string } {
 const out: { [path: string]: string } = {};

 const walk = (node: FileSystemTree, prefix: string) => {
  for (const [name, entry] of Object.entries(node)) {
   const path = prefix ? `${prefix}/${name}` : name;
   if (entry && typeof entry === 'object' && 'file' in entry) {
    const contents = (entry as { file: { contents: string | Uint8Array } }).file.contents;
    out[path] =
     typeof contents === 'string' ? contents : new TextDecoder().decode(contents);
   } else if (entry && typeof entry === 'object' && 'directory' in entry) {
    walk((entry as { directory: FileSystemTree }).directory, path);
   }
  }
 };

 walk(tree, '');
 return out;
}

/**
 * Derive the flat File_Contents default map from a Source_Tree. Folder nodes are
 * skipped; each file node contributes `path -> content` (paths preserved as
 * declared, e.g. `/src/app.ts`). Files with no content default to an empty
 * string.
 */
export function extractFileContents(fileStructure: FileTemplate[]): { [path: string]: string } {
 const out: { [path: string]: string } = {};

 const walk = (nodes: FileTemplate[]) => {
  for (const node of nodes) {
   if (node.type === 'file') {
    out[node.path] = node.content ?? '';
   }
   if (node.children && node.children.length > 0) {
    walk(node.children);
   }
  }
 };

 walk(fileStructure);
 return out;
}
